import ptBR from "../content/pt-BR.js";
import type { PageContent, SplitCopy } from "../content/types.js";
import type { AccentId } from "../demo-v2/types.js";
import { whatsappTo } from "../shared/links.js";
import type { MenuIconName } from "./icons.js";

export interface LiveCopy {
  readonly label: string;
  readonly event: string;
  readonly live: string;
  readonly count: string;
  readonly sent: string;
  readonly minutes: string;
  readonly join: string;
}

export interface NumberCopy {
  readonly value: number;
  readonly unit?: string;
  readonly label: string;
}

export interface FlowStep {
  readonly tag: string;
  readonly title: string;
  readonly body: string;
}

export interface FlowCopy {
  readonly label: string;
  readonly title: string;
  readonly steps: readonly FlowStep[];
  readonly scan: string;
  readonly open: string;
  readonly invitedBy: string;
  readonly headline: readonly [string, string];
  readonly first: string;
  readonly last: string;
  readonly guest: readonly [string, string];
  readonly enter: string;
  readonly noApp: string;
  readonly left: string;
  readonly sentToWall: string;
  readonly hint: string;
  readonly nudge: string;
  readonly nudgeLast: string;
  readonly scrollNext: string;
  readonly scrollCue: string;
  readonly next: string;
  readonly stepOf: string;
}

export interface WallCopy {
  readonly live: string;
  readonly count: string;
  readonly now: string;
  readonly join: string;
  readonly point: string;
  readonly madeWith: string;
  readonly waiting: string;
  readonly kept: string;
  readonly label: string;
}

export interface ControlCopy extends SplitCopy {
  readonly reveal: string;
  readonly revealNow: string;
  readonly revealEnd: string;
  readonly revealNowHint: string;
  readonly revealEndHint: string;
  readonly recent: string;
  readonly hide: string;
  readonly show: string;
  readonly hidden: string;
  readonly hideLabel: string;
  readonly showLabel: string;
  readonly event: string;
}

export interface AlbumCopy extends SplitCopy {
  readonly all: string;
  readonly photos: string;
  readonly original: string;
}

export interface StudioDemoCopy {
  readonly cta: string;
  readonly note: string;
  readonly qrTitle: string;
  readonly qrBody: string;
}

export interface StudioCopy extends SplitCopy {
  readonly names: readonly string[];
  readonly nameLabel: string;
  readonly colorLabel: string;
  readonly colorNames: Readonly<Record<AccentId, string>>;
  readonly demo: StudioDemoCopy;
}

export interface MenuItem {
  readonly href: string;
  readonly icon: MenuIconName;
  readonly title: string;
  readonly body?: string;
  readonly external?: true;
}

export interface MenuCopy {
  readonly product: string;
  readonly explore: string;
  readonly items: readonly MenuItem[];
  readonly links: readonly MenuItem[];
  readonly start: string;
  readonly startHref?: string;
  readonly talk: string;
}

export interface V2FootCopy {
  readonly demo?: { readonly label: string; readonly href: string };
  readonly title: string;
  readonly note: string;
  readonly action: string;
  readonly rights: string;
}

export interface V2Content extends PageContent {
  readonly menu: MenuCopy;
  readonly live: LiveCopy;
  readonly numbers: readonly NumberCopy[];
  readonly flow: FlowCopy;
  readonly wall: WallCopy;
  readonly control: ControlCopy;
  readonly album: AlbumCopy;
  readonly studio: StudioCopy;
  readonly ending: V2FootCopy;
}

export const HERO_SLIDES: readonly string[] = [
  "/assets/photos/hero-danca.webp",
  "/assets/photos/hero-jardim.webp",
  "/assets/photos/hero-petalas.webp",
  "/assets/photos/hero-primeira-danca.webp",
];

const content: V2Content = {
  ...ptBR,
  meta: {
    ...ptBR.meta,
    title: "Kuori — o evento inteiro, por quem esteve nele",
  },
  nav: { ...ptBR.nav, short: "Começar agora" },
  menu: {
    product: "Produto",
    explore: "Explore a Kuori",
    items: [
      {
        href: "#como",
        icon: "qr",
        title: "Como funciona",
        body: "Do QR ao telão, em segundos.",
      },
      {
        href: "#produto",
        icon: "monitor",
        title: "O telão",
        body: "Revele na hora ou no fim. Oculte pelo celular.",
      },
      {
        href: "#album",
        icon: "images",
        title: "O álbum",
        body: "Pronto no dia seguinte, hora por hora.",
      },
      {
        href: "#marca",
        icon: "palette",
        title: "Marca branca",
        body: "Seu nome e a sua cor no convite e no telão.",
      },
    ],
    links: [
      {
        href: whatsappTo("Oi! Quero um orçamento da Kuori para o meu evento."),
        icon: "whatsapp",
        title: "Pedir orçamento",
        external: true,
      },
      { href: "#faq", icon: "help", title: "Perguntas" },
    ],
    start: "Começar agora",
    startHref: whatsappTo("Oi! Quero começar a usar a Kuori."),
    talk: "Fale com a Kuori",
  },
  hero: {
    ...ptBR.hero,
    sub: "O convidado aponta a câmera para o QR, fotografa pelo navegador e a foto vai para o álbum e para o telão em segundos. Sem instalar nada.",
    alt: "Ver como funciona",
    slides: HERO_SLIDES,
  },
  live: {
    label: "Telão · exemplo",
    event: "Casamento Leo & Day",
    live: "ao vivo",
    count: "fotos no álbum",
    sent: "enviou uma foto",
    minutes: "há {n} min",
    join: "Aponte a câmera para o QR",
  },
  numbers: [
    { value: 0, label: "aplicativos para o convidado instalar" },
    { value: 1, label: "QR para o evento inteiro, do convite ao telão" },
    { value: 60, unit: "s", label: "para criar o evento e imprimir o QR" },
  ],
  flow: {
    label: "Como funciona",
    title: "Do QR ao telão, em segundos.",
    steps: [
      {
        tag: "Aponte",
        title: "O QR abre o evento.",
        body: "Ele fica no convite, no cartão de mesa e no telão. A câmera do celular reconhece e abre o evento no navegador.",
      },
      {
        tag: "Entre",
        title: "Nome e sobrenome. Só.",
        body: "Sem cadastro, sem senha e sem aplicativo. Funciona até de dentro do WhatsApp e do Instagram.",
      },
      {
        tag: "Fotografe",
        title: "A câmera do evento, na hora.",
        body: "Cada convidado tem a sua cota de fotos. A foto sai em qualidade original, sem compressão de grupo.",
      },
      {
        tag: "No telão",
        title: "E todo mundo vê.",
        body: "A foto entra no álbum e aparece no telão com o nome de quem tirou, enquanto o evento acontece.",
      },
    ],
    scan: "Casamento Leo & Day",
    open: "Abrir no navegador",
    invitedBy: "Convidado por",
    headline: ["As fotos do evento, ", "por você"],
    first: "Nome",
    last: "Sobrenome",
    guest: ["Ana", "Souza"],
    enter: "Entrar no evento",
    noApp: "Sem instalar aplicativo.",
    left: "8 de 10 fotos",
    sentToWall: "Enviada. Já está no telão.",
    hint: "Role para seguir",
    nudge: "Arraste para ver o próximo passo",
    nudgeLast: "Continue rolando",
    scrollNext: "Role para o próximo passo",
    scrollCue: "Role",
    next: "Próximo",
    stepOf: "{n} de {total}",
  },
  wall: {
    live: "ao vivo",
    count: "Fotos no álbum",
    now: "Agora mesmo",
    join: "Entre no álbum e mande sua foto",
    point: "aponte a câmera",
    madeWith: "feito com",
    waiting: "As fotos aparecem às 23h",
    kept: "fotos guardadas para a revelação",
    label:
      "Ilustração do telão: a foto mais recente em destaque, as anteriores ao lado e o QR do evento",
  },
  control: {
    label: "No dia",
    title: "O telão obedece a você.",
    body: "Revele as fotos na hora ou só no fim. Oculte uma foto em segundos, pelo celular, e ela sai do telão na mesma hora. Experimente aqui.",
    reveal: "Revelação",
    revealNow: "Na hora",
    revealEnd: "No fim",
    revealNowHint: "Cada foto aparece no telão assim que chega.",
    revealEndHint: "As fotos ficam guardadas e aparecem juntas no fim.",
    recent: "Fotos recentes",
    hide: "Ocultar",
    show: "Mostrar",
    hidden: "Oculta",
    hideLabel: "Ocultar a foto de {name}",
    showLabel: "Mostrar a foto de {name}",
    event: "Casamento Leo & Day",
  },
  album: {
    label: "Depois",
    title: "O álbum pronto, hora por hora.",
    body: "No dia seguinte, cada foto está no álbum, separada pela hora em que foi tirada e em qualidade original. Toque numa hora.",
    all: "Tudo",
    photos: "fotos",
    original: "Qualidade original",
  },
  studio: {
    label: "Marca branca",
    title: "Seu nome na frente. O nosso, fora do caminho.",
    body: "Profissionais de eventos entregam a Kuori com o próprio nome e a própria cor, no convite e no telão. Troque abaixo e veja.",
    names: ["Ateliê Marília", "Studio Aurora", "Casa Lume"],
    nameLabel: "Nome",
    colorLabel: "Cor",
    colorNames: { pink: "Rosa", blue: "Azul", green: "Verde", amber: "Âmbar", ink: "Preto" },
    demo: {
      cta: "Ver na demonstração",
      note: "A demonstração completa começa com o nome e a cor que você escolheu. Uns dois minutos, sem cadastro.",
      qrTitle: "Prefere no celular?",
      qrBody: "Aponte a câmera para abrir a demonstração.",
    },
  },
  ending: {
    title: "Vamos fotografar o seu próximo evento?",
    note: "Respondemos no mesmo dia útil.",
    action: "Falar no WhatsApp",
    rights: "2026 Kuori",
  },
};

export default content;
